import React, { useState, useMemo } from 'react'

const FavoriteServiceModal = ({
  isOpen,
  onClose,
  service,
  specialists,
  categories,
  onConfirm
}) => {
  const [selectedProfessional, setSelectedProfessional] = useState(null)

  // Find the service's category
  const serviceCategory = useMemo(() => {
    if (!service || !categories) return null
    if (!service || !categories) return null
    
    // Check if service has category_id
    if (service.category_id) {
      return categories.find(cat => cat.id === service.category_id)
    }
    
    // Check if service has category_name
    if (service.category_name) {
      return categories.find(cat => cat.name === service.category_name)
    }
    
    // Search through categories to find which one contains this service
    for (const category of categories) {
      if (category.services && Array.isArray(category.services)) {
        const found = category.services.find(s => s.id === service.id)
        if (found) return category
      }
    }
    
    return null
  }, [service, categories])

  // Filter professionals who specialize in this service's category
  const relevantProfessionals = useMemo(() => {
    if (!specialists || specialists.length === 0) return []
    if (!specialists || specialists.length === 0) return []
    
    // If no category found, show all professionals
    if (!serviceCategory) {
      return specialists.filter(s => s.id !== 'any')
    }
    
    const categoryName = serviceCategory.name?.toLowerCase()
    const categoryId = serviceCategory.id
    
    return specialists.filter(specialist => {
      // Exclude "any" option
      if (specialist.id === 'any') return false
      
      // If professional has no specializations, include as fallback
      if (!specialist.specializations || !Array.isArray(specialist.specializations) || specialist.specializations.length === 0) {
        return true
      }
      
      // Check if professional specializes in this category
      return specialist.specializations.some(spec => {
        // spec can be a string (category name) or object
        if (typeof spec === 'string') {
          return spec.toLowerCase() === categoryName
        }
        
        // Handle object format
        if (typeof spec === 'object' && spec !== null) {
          const specName = spec.name || spec.category_name
          const specId = spec.id || spec.category_id
          return (specName && specName.toLowerCase() === categoryName) ||
                 (specId && String(specId) === String(categoryId))
        }
        
        return false
      })
    })
  }, [specialists, serviceCategory])

  // If no professionals match, fallback to all professionals
  const displayProfessionals = relevantProfessionals.length > 0 
    ? relevantProfessionals 
    : (specialists || []).filter(s => s.id !== 'any')

  const handleConfirm = () => {
    if (selectedProfessional) {
      onConfirm(selectedProfessional)
      setSelectedProfessional(null)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900">Select Professional</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>
        
        <p className="text-gray-600 mb-4 flex-shrink-0">
          Choose a professional for <span className="font-semibold">{service?.name}</span>
        </p>

        <div className="flex-1 min-h-0 overflow-y-auto mb-4 space-y-2">
          {displayProfessionals && displayProfessionals.length > 0 ? (
            displayProfessionals.map((specialist) => (
              <button
                key={specialist.id}
                onClick={() => setSelectedProfessional(specialist)}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  selectedProfessional?.id === specialist.id
                    ? 'border-primary bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={specialist.image || specialist.image_url || 'https://via.placeholder.com/60'}
                    alt={specialist.name}
                    className="w-12 h-12 rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/60'
                    }}
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{specialist.name}</p>
                    <p className="text-sm text-gray-500">{specialist.role || specialist.specialty}</p>
                  </div>
                  {selectedProfessional?.id === specialist.id && (
                    <i className="fas fa-check-circle text-primary text-xl"></i>
                  )}
                </div>
              </button>
            ))
          ) : (
            <p className="text-gray-500 text-center py-4">No professionals available</p>
          )}
        </div>

        <div className="flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedProfessional}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-purple-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add to Favorites
          </button>
        </div>
      </div>
    </div>
  )
}

export default FavoriteServiceModal

